import { useCallback, useId, useState } from 'react';

import {
  AdminSectionCard,
  ConfirmDialog,
  HelpAnchor,
  InlineError,
  Pill,
  SkeletonBlock,
  Switch,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { SEED_WORKSPACE_CONFIG } from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  ACCEPT_ARBITRARY_HINT,
  acceptArbitraryEffectiveLabel,
  acceptArbitrarySourceBadge,
  nextAcceptArbitrary,
  nextWorkspaceEnabled,
} from './WorkspaceScene.transform';

export function WorkspaceScene() {
  const acceptArbitraryHintId = useId();
  const [config, setConfig] = useState(SEED_WORKSPACE_CONFIG);
  const [loading] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptArbitraryConfirmOpen, setAcceptArbitraryConfirmOpen] =
    useState(false);

  const toggleEnabled = useCallback(() => {
    setToggleBusy(true);
    setError(null);
    setConfig((prev) => nextWorkspaceEnabled(prev));
    setToggleBusy(false);
  }, []);

  const commitAcceptArbitrary = useCallback((nextValue: boolean) => {
    setToggleBusy(true);
    setError(null);
    setConfig((prev) => nextAcceptArbitrary(prev, nextValue));
    setToggleBusy(false);
    return true;
  }, []);

  const handleAcceptArbitrarySwitch = useCallback(
    (next: boolean) => {
      if (toggleBusy) return;
      if (next) {
        setAcceptArbitraryConfirmOpen(true);
        return;
      }
      commitAcceptArbitrary(false);
    },
    [commitAcceptArbitrary, toggleBusy],
  );

  const sourceBadge = acceptArbitrarySourceBadge(config.source);
  const effectiveLabel = acceptArbitraryEffectiveLabel(config.acceptArbitraryUploads);

  return (
    <AdminSceneShell
      sceneName="WorkspaceScene"
      title="Workspace"
      description="Workspace-wide upload and feature toggles."
      headerActions={<HelpAnchor doc="workspace/admin" />}
      stackClassName="space-y-3"
    >
      <Commentable name="Panel">
        <div className="space-y-3">
          <AdminSectionCard
            title="Show workspace surfaces"
            description="Controls Projects, Dashboards, Tasks, and Editor — not the AI chat tab."
            actions={
              loading ? (
                <SkeletonBlock className="h-5 w-9 rounded-full" />
              ) : (
                <Switch
                  id="workspace-enabled"
                  checked={config.enabled}
                  onCheckedChange={toggleEnabled}
                  disabled={toggleBusy}
                  aria-label="Show workspace surfaces"
                />
              )
            }
          />

          <AdminSectionCard
            title="Accept arbitrary upload types"
            actions={
              loading ? (
                <SkeletonBlock className="h-5 w-9 rounded-full" />
              ) : (
                <>
                  <Pill tone={config.acceptArbitraryUploads ? 'success' : 'default'}>
                    {effectiveLabel}
                  </Pill>
                  <Pill>{sourceBadge}</Pill>
                  <Switch
                    id="workspace-accept-arbitrary"
                    checked={config.acceptArbitraryUploads}
                    onCheckedChange={handleAcceptArbitrarySwitch}
                    disabled={toggleBusy}
                    aria-label="Accept arbitrary upload types"
                    aria-describedby={acceptArbitraryHintId}
                  />
                </>
              )
            }
          >
            <p id={acceptArbitraryHintId} className="text-sm text-muted">
              {ACCEPT_ARBITRARY_HINT}
            </p>
            {config.source === 'db' ? (
              <p className="mt-2 text-xs text-muted">
                Stored in database; env ignored once set.
              </p>
            ) : null}
          </AdminSectionCard>

          {error && !acceptArbitraryConfirmOpen ? (
            <InlineError>{error}</InlineError>
          ) : null}

          <ConfirmDialog
            open={acceptArbitraryConfirmOpen}
            onOpenChange={(open) => {
              setAcceptArbitraryConfirmOpen(open);
              if (!open) setError(null);
            }}
            error={acceptArbitraryConfirmOpen ? error ?? undefined : undefined}
            title="Enable arbitrary upload types?"
            description={
              <>
                <span className="block">
                  The MIME denylist still applies — denylisted types cannot be staged.
                </span>
                <span className="mt-2 block">
                  R9 serve hardening still applies (SVG-XSS exclusion, nosniff). Staging a
                  type is not the same as safe arbitrary rendering.
                </span>
              </>
            }
            confirmLabel="Enable"
            onConfirm={async () => {
              const ok = commitAcceptArbitrary(true);
              if (!ok) throw new Error('persist failed');
            }}
          />
        </div>
      </Commentable>
    </AdminSceneShell>
  );
}
